/**
 * Copy Container # in groups.
 *
 * The terminal sites only accept so many container numbers in one lookup, so
 * pasting all 452 is not a thing anyone can do (Alan, 2026-08-08). The number in
 * this panel is therefore the **group size**, not a total: enter 10 against 452
 * matching containers and the system cuts them into 46 groups of 10, and you copy
 * group 1, go and check it, come back, copy group 2.
 *
 * That is why the panel stays open while you step through it. Closing on every
 * copy would mean reopening and re-finding your place 46 times, and the place is
 * the whole point - the panel remembers which group you are on and moves to the
 * next one the moment you copy the current one.
 */

import { setButtonBusy, submitButtonFor } from './busy-button.mjs?v=20260806.100';

function escapeHtml(value) {
  return String(value ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');
}

/**
 * A usable group size, or null.
 *
 * Zero and negatives are a typo rather than "no grouping", and a size at or above
 * the ceiling means one group of everything, which is what null says downstream.
 */
export function normalizeCopyCount(value, { maxItems = 5000 } = {}) {
  if (value === null || value === undefined || value === '') return null;
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return null;
  const rounded = Math.floor(numeric);
  if (rounded < 1) return null;
  return rounded >= maxItems ? null : rounded;
}

/** How many groups a fleet of `total` falls into at `size` each. */
export function groupCount(total, size) {
  const items = Number(total);
  const perGroup = Number(size);
  if (!Number.isFinite(items) || items < 1) return 0;
  if (!Number.isFinite(perGroup) || perGroup < 1) return 1;
  return Math.ceil(items / perGroup);
}

/** The slice of `numbers` belonging to group `index`, counted from zero. */
export function groupSlice(numbers, size, index) {
  const list = Array.isArray(numbers) ? numbers : [];
  const perGroup = Number(size);
  if (!Number.isFinite(perGroup) || perGroup < 1) return [...list];
  const start = Math.max(0, Math.floor(index)) * perGroup;
  return list.slice(start, start + perGroup);
}

const COPY = Object.freeze({
  zh: Object.freeze({
    size: '每组数量',
    hint: (total) => `共 ${total} 个柜号`,
    unknown: '按当前筛选的全部柜号',
    split: (groups, size) => `分成 ${groups} 组，每组 ${size} 个`,
    position: (index, groups) => `第 ${index} / ${groups} 组`,
    copyGroup: '复制这一组',
    copyAll: '全部复制',
    previous: '上一组',
    next: '下一组',
    copied: (index) => `已复制第 ${index} 组`,
    done: '已复制到最后一组',
    marked: (count) => `已标记 ${count} 个`,
    clearMarks: '清除标记',
  }),
  en: Object.freeze({
    size: 'Per group',
    hint: (total) => `${total} container numbers`,
    unknown: 'All matching container numbers',
    split: (groups, size) => `${groups} groups of ${size}`,
    position: (index, groups) => `Group ${index} of ${groups}`,
    copyGroup: 'Copy this group',
    copyAll: 'Copy all',
    previous: 'Previous',
    next: 'Next',
    copied: (index) => `Group ${index} copied`,
    done: 'Last group copied',
    marked: (count) => `${count} marked`,
    clearMarks: 'Clear marks',
  }),
});

function copyFor(locale) {
  return String(locale).toLowerCase().startsWith('zh') ? COPY.zh : COPY.en;
}

export function copyMenuMarkup({
  total = null, maxItems = 5000, locale = 'zh', marked = 0,
} = {}) {
  const copy = copyFor(locale);
  const known = Number.isSafeInteger(total) && total > 0;
  return `<form class="dashboard-copy-menu" data-copy-panel>
      <label class="dashboard-copy-menu__field">
        <span>${escapeHtml(copy.size)}</span>
        <input name="size" type="number" min="1" max="${maxItems}" step="1"
          inputmode="numeric" autocomplete="off" data-copy-size>
      </label>
      <p class="dashboard-copy-menu__hint" data-copy-hint>${escapeHtml(known ? copy.hint(total) : copy.unknown)}</p>
      <div class="dashboard-copy-menu__stepper" data-copy-stepper hidden>
        <button type="button" data-copy-step="-1" aria-label="${escapeHtml(copy.previous)}">‹</button>
        <span data-copy-position></span>
        <button type="button" data-copy-step="1" aria-label="${escapeHtml(copy.next)}">›</button>
      </div>
      <div class="dashboard-copy-menu__actions">
        <button type="button" class="dashboard-copy-menu__all" data-copy-all>${escapeHtml(copy.copyAll)}</button>
        <button type="submit" data-copy-submit>${escapeHtml(copy.copyGroup)}</button>
      </div>
      ${/* The only way the yellow marks go (Alan, 2026-10-17): they stay through reloads and searches until this. */ ''}
      <button type="button" class="dashboard-copy-menu__clear" data-copy-clear${marked > 0 ? '' : ' hidden'}>${escapeHtml(copy.clearMarks)} (${escapeHtml(copy.marked(marked))})</button>
    </form>`;
}

export function mountDashboardCopyMenu({
  trigger, overlayManager, onCopy, onCopyAll, total = () => null, maxItems = 5000,
  locale = () => 'zh',
  // The rows already sent to the clipboard stay marked in the table until they
  // are cleared from here (Alan, 2026-08-09).
  markedCount = () => 0, onClearMarks = null,
  // Highlights the group the stepper is on, so you can see which slice you are
  // about to take before you take it (Alan, 2026-08-11).
  onPreviewGroup = null,
}) {
  if (!trigger || typeof overlayManager?.open !== 'function' || typeof onCopy !== 'function') {
    throw new TypeError('Dashboard copy menu options are invalid.');
  }
  const documentRef = trigger.ownerDocument;

  function open() {
    const activeLocale = typeof locale === 'function' ? locale() : locale;
    const copy = copyFor(activeLocale);
    const matching = total();
    const panel = documentRef.createElement('div');
    panel.innerHTML = copyMenuMarkup({
      total: matching, maxItems, locale: activeLocale, marked: Number(markedCount()) || 0,
    });
    const overlay = overlayManager.open({
      anchor: trigger,
      content: panel,
      modal: false,
      kind: 'copy-count',
      role: 'dialog',
      ariaLabel: copy.size,
      // Closing the panel clears the marks (Alan, 2026-08-11: "复制完成后点出tab
      // 就应该清楚这些highlight"). They exist to show which rows went out while
      // you work through the groups; once the panel is shut that job is over, and
      // a table still wearing them the next time you open it is stale paint
      // rather than information. This replaces the 2026-08-09 rule that they
      // survive until cleared by hand - the hand-clear button stays for clearing
      // mid-session.
      onClose() {
        onClearMarks?.();
      },
    });
    const scope = overlay?.querySelector ? overlay : panel;
    const form = scope.querySelector('[data-copy-panel]');
    const sizeInput = scope.querySelector('[data-copy-size]');
    const stepper = scope.querySelector('[data-copy-stepper]');
    const position = scope.querySelector('[data-copy-position]');
    const hint = scope.querySelector('[data-copy-hint]');
    const clear = scope.querySelector('[data-copy-clear]');
    let index = 0;

    // The panel stays open across a copy, so the marked count it shows has to
    // keep up with the group that just went out.
    const renderMarks = () => {
      if (!clear) return;
      const count = Number(markedCount()) || 0;
      clear.hidden = count < 1;
      clear.textContent = `${copy.clearMarks} (${copy.marked(count)})`;
    };

    const size = () => normalizeCopyCount(sizeInput?.value, { maxItems });
    const groups = () => groupCount(matching, size());

    const render = () => {
      const perGroup = size();
      const count = groups();
      // No size yet, or one group of everything: there is nothing to step
      // through, and a stepper reading "1 of 1" is furniture.
      const stepped = Boolean(perGroup) && count > 1;
      if (stepper) stepper.hidden = !stepped;
      index = Math.max(0, Math.min(index, Math.max(0, count - 1)));
      if (position) position.textContent = copy.position(index + 1, count);
      if (hint && Number.isSafeInteger(matching) && matching > 0) {
        hint.textContent = perGroup ? copy.split(count, perGroup) : copy.hint(matching);
      }
      onPreviewGroup?.(perGroup, index);
    };

    sizeInput?.addEventListener('input', () => { index = 0; render(); });
    scope.addEventListener('click', (event) => {
      const step = event.target.closest?.('[data-copy-step]');
      if (step) {
        index += Number(step.dataset.copyStep) || 0;
        render();
        return;
      }
      if (event.target.closest?.('[data-copy-clear]')) {
        onClearMarks?.();
        renderMarks();
        return;
      }
      if (!event.target.closest?.('[data-copy-all]')) return;
      overlayManager.close('copy');
      (typeof onCopyAll === 'function' ? onCopyAll : onCopy)(null, 0);
    });

    form?.addEventListener('submit', (event) => {
      event.preventDefault();
      const perGroup = size();
      // A blank or nonsense size leaves the panel alone rather than quietly
      // copying everything, which is the one thing nobody asked for.
      if (!perGroup && String(sizeInput?.value ?? '').trim() !== '') return;
      const at = index;
      // Advance before the copy resolves: the next group is where you want to be
      // when you come back from checking this one.
      if (perGroup && index + 1 < groups()) index += 1;
      render();
      const copying = onCopy(perGroup, at);
      // Hand the button back the moment the clipboard write lands.
      //
      // mountGlobalBusyButtons disables every submit button on submit and only
      // frees it when the person touches the form again or a 20-second backstop
      // fires. That is right for a page that navigates away and wrong here: this
      // panel stays open, a clipboard write takes about ten milliseconds, and the
      // copy button sat dead for the next twenty seconds (Alan, 2026-08-11: "copy
      // 之后要显示这样子很久根本再次点击不了copy"). Copying group after group is
      // the whole point of the panel.
      const submit = submitButtonFor(form);
      // The marks are stamped by whoever handled the copy, so the count is read
      // back once it has settled rather than guessed here.
      Promise.resolve(copying).then(renderMarks, renderMarks).finally(() => {
        setButtonBusy(submit, false);
      });
    });

    sizeInput?.focus?.();
    render();
    renderMarks();
  }

  trigger.addEventListener('click', open);
  return {
    open,
    destroy() {
      trigger.removeEventListener('click', open);
      overlayManager.close('destroy');
    },
  };
}
